import { Record, Map } from 'immutable';

import { ActionTypes } from '../constants/AppConstants';
import BaseStore from './BaseStore';
import FeatureFlagStore from './FeatureFlagStore';

export const LinkedSmartcardTypes = {
    ADULTS: 'adults',
    CHILDREN: 'children',
};

export const hasLinkedSmartcard = ({ linkedSmartcards, smartcard }) => {
    return linkedSmartcards
        .map(link => link.getIn(['smartcard', 'associatedSmartcardNumber']))
        .toList()
        .includes(smartcard.associatedSmartcardNumber);
};

const DefaultState = new Record({
    [LinkedSmartcardTypes.ADULTS]: new Map(),
    [LinkedSmartcardTypes.CHILDREN]: new Map(),
});

export class LinkingSmartcardsStore extends BaseStore {
    getInitialState() {
        return new DefaultState();
    }

    getActionHandlers() {
        return {
            [ActionTypes.LINKING_SMARTCARDS_LINK]: this.handleLink,
            [ActionTypes.ORDER_CREATED]: this.resetState,
            [ActionTypes.ORDER_UPDATED]: this.handleOrderUpdated,
        };
    }

    getAllLinkedSmartcards() {
        return this.getAdultsLinkedSmartcards().merge(this.getChildrenLinkedSmartcards());
    }

    getAdultsLinkedSmartcards() {
        return this.state.get(LinkedSmartcardTypes.ADULTS);
    }

    getAdultsLinkedSmartcardsForFare(ids) {
        return this.getAdultsLinkedSmartcards()
            .filter(linked => ids.includes(linked.getIn(['ticket', 'id'])))
            .map(result => result.get('smartcard'))
            .sortBy(result => result.get('customerName'))
            .toList();
    }

    getChildrenLinkedSmartcards() {
        return this.state.get(LinkedSmartcardTypes.CHILDREN);
    }

    getChildrenLinkedSmartcardsForFare(ids) {
        return this.getChildrenLinkedSmartcards()
            .filter(linked => ids.includes(linked.getIn(['ticket', 'id'])))
            .map(result => result.get('smartcard'))
            .sortBy(result => result.get('customerName'))
            .toList();
    }

    linkSmartcard({ type, smartcard, ticket }) {
        const linkedSmartcards = this.state.get(type);

        let newState = linkedSmartcards.filter(
            link =>
                smartcard.associatedSmartcardNumber !==
                link.getIn(['smartcard', 'associatedSmartcardNumber']),
        );

        newState = newState.set(ticket.id, new Map({ smartcard, ticket }));

        this.setState(this.state.set(type, newState));
    }

    unlinkSmartcard({ type, ticket }) {
        const linkedSmartcards = this.state.get(type);

        let newState = linkedSmartcards.delete(ticket.id);

        this.setState(this.state.set(type, newState));
    }

    handleLink({ smartcard, ticket }) {
        const type =
            ticket.children > 0 && ticket.adults === 0
                ? LinkedSmartcardTypes.CHILDREN
                : LinkedSmartcardTypes.ADULTS;

        if (smartcard) {
            this.linkSmartcard({ type, smartcard, ticket });
        } else {
            this.unlinkSmartcard({ type, ticket });
        }
    }

    handleOrderUpdated({ order }) {
        const linkingSmartcardsEnabled = FeatureFlagStore.get('enableSmartcardAssociations');

        if (!linkingSmartcardsEnabled) {
            return;
        }

        const tickets = order.getTickets();

        [LinkedSmartcardTypes.ADULTS, LinkedSmartcardTypes.CHILDREN].map(type => {
            const linkedSmartcards = this.state.get(type);

            // Remove a linked smartcard if a ticket is no longer in the basket
            const newState = linkedSmartcards.filter((link, ticketId) =>
                tickets.some(ticket => ticket.id === ticketId),
            );

            this.setState(this.state.set(type, newState));
        });
    }

    resetState() {
        return this.setState(this.getInitialState());
    }
}

export default new LinkingSmartcardsStore({
    storageKey: 'linking-smartcards',
    expires: 30 * 60
});
